import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faFile } from "@fortawesome/pro-regular-svg-icons";
import { AxiosError } from "axios";

import { useHistoryDecisionV3 } from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { useFile } from "src/api/filesApi/queries";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill, PillVariants } from "src/base-components/Pill";
import {
  GetFileEnvFilesFileIdGetEnvEnum,
  JsonApiResourceObjectFileAttributesFileMetaData,
} from "src/clients/files";
import { FileViewer } from "src/files/FileViewer";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { PLACEHOLDER_FILE_ID } from "src/utils/files";

export const FilePill = ({
  decisionId,
  env,
  fileId,
  monospaced = true,
}: {
  decisionId?: string;
  env?: GetFileEnvFilesFileIdGetEnvEnum;
  fileId: string;
  monospaced?: boolean;
}) => {
  const { workspace } = useWorkspaceContext();
  const decisionTrace = useHistoryDecisionV3({
    baseUrl: workspace.base_url!,
    decisionId: decisionId ?? "",
  });
  let decisionEnvironment: string | undefined;
  if (env) {
    decisionEnvironment = env;
  } else if (decisionId === undefined) {
    decisionEnvironment = "sandbox";
  } else if (
    !decisionTrace.data?.isQueryError &&
    decisionTrace.data?.decision
  ) {
    decisionEnvironment = decisionTrace.data.decision.environment;
  } else {
    decisionEnvironment = undefined;
  }

  const fontType = monospaced ? "code-12" : "text";

  const {
    data: file,
    isLoading,
    error,
  } = useFile(
    fileId,
    decisionEnvironment as GetFileEnvFilesFileIdGetEnvEnum | undefined,
    workspace.base_url!,
  );

  if (fileId === PLACEHOLDER_FILE_ID) {
    return (
      <div className="inline-block">
        <FileViewer file={undefined}>
          <BaseFilePill fileName="File" fontType={fontType} />
        </FileViewer>
      </div>
    );
  }
  if (isLoading || !decisionEnvironment) {
    return <BaseFilePill fileName="Loading file..." fontType={fontType} />;
  }
  if (
    (error instanceof AxiosError && error.response?.status === 404) ||
    !file?.data.data?.attributes
  ) {
    return <BaseFilePill fileName="File not found" fontType={fontType} />;
  }
  if (error) {
    return (
      <BaseFilePill
        fileName="Error fetching file"
        fontType={fontType}
        variant="red"
      />
    );
  }

  return (
    <div className="inline-block">
      <FileViewer file={file.data.data.attributes}>
        <BaseFilePill
          copyValue={fileId}
          fileName={getFileName(file.data.data, 12)}
          fontType={fontType}
        />
      </FileViewer>
    </div>
  );
};

type BaseFilePillProps = {
  fileName: string;
  variant?: Extract<PillVariants, "gray" | "red">;
  icon?: IconProp;
  fontType?: "code-12" | "text";
  onClick?: () => void;
  copyValue?: string;
};

export const BaseFilePill: React.FC<BaseFilePillProps> = ({
  icon = faFile,
  variant = "gray",
  fileName,
  fontType = "code-12",
  onClick,
  copyValue,
}) => {
  return (
    <Pill size="sm" variant={variant} onClick={onClick}>
      <Pill.Icon icon={icon} />
      <Pill.Text fontType={fontType}>{fileName}</Pill.Text>
      {copyValue && (
        <CopyTextIcon
          dataLoc="copy-file-id"
          feedback="inline"
          size="2xs"
          tooltip="Copy file ID"
          value={copyValue}
        />
      )}
    </Pill>
  );
};

export const getFileName = (
  file: JsonApiResourceObjectFileAttributesFileMetaData,
  maxLength = 15,
) => {
  const fileName = file.attributes.name;

  const safeMax = Math.max(maxLength, 5);
  if (fileName.length > safeMax) {
    const startLen = Math.ceil((safeMax - 3) / 2);
    const endLen = Math.floor((safeMax - 3) / 2);
    return `${fileName.slice(0, startLen)}...${fileName.slice(-endLen)}`;
  }
  return fileName;
};
